import { useMutation } from '@vue/apollo-composable';
import { gql } from 'graphql-tag';
import { useToast } from 'primevue/usetoast';

// Retrieve UploadFileDocument from existing definitions or define locally if strictly needed.
// Based on check, it was in '@/graphql/Forms'. We can import it or redefine it.
import { useUploadFileMutation, UploadFileDocument } from '@/types/vue-apollo-helpers';
// Importing is better to DRY.

export const useFileUpload = () => {
  const toast = useToast();
  const uploading = ref(false);

  const { mutate: uploadMutate } = useUploadFileMutation();

  const uploadFile = async(file: File) => {
    uploading.value = true;
    try {
      const res = await uploadMutate({ file: file });
      // toast.add({ severity: 'success', summary: 'Success', detail: 'File uploaded successfully' });
      return res?.data?.uploadFile;
    } catch (e) {
      toast.add({ severity: 'error', summary: 'Upload Failed', detail: e instanceof Error ? e.message : 'Failed to upload file.' });
      throw e;
    } finally {
      uploading.value = false;
    }
  };

  const uploadFiles = async(files: File[]) => {
    uploading.value = true;
    const uploadedIds: string[] = [];
    try {
      // Upload sequentially or parallel. Parallel is faster.
      const promises = files.map(file => uploadMutate({ file }));
      const results = await Promise.all(promises);

      results.forEach(res => {
        if (res?.data?.uploadFile?.id) {
          uploadedIds.push(res.data.uploadFile.id);
        }
      });
      return uploadedIds;
    } catch (e) {
      toast.add({ severity: 'error', summary: 'Upload Failed', detail: 'One or more files failed to upload.' });
      throw e;
    } finally {
      uploading.value = false;
    }
  };

  return {
    uploadFile,
    uploadFiles,
    uploading,
  };
};
